import React, { useState, memo, useEffect, useCallback, useRef, ChangeEvent } from "react";
import { ArrowLeftIcon, ImageIcon, PlusCircle, Save, Trash2, XCircle } from "lucide-react";
import { IconButton, Slider, Tooltip } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { FormModel } from "../models/form.model";
import { useSnackbar } from "../contexts/SnackbarContext";
import { useAuthenticatedFetch } from "../utils/auth-fetch";

// Types
type FieldType = "text" | "number" | "email" | "checkbox" | "select" | "textarea" | "radio" | "info" | "image-info";
type TextStyle = "normal" | "bold" | "italic";

interface FormField {
  id: string;
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  content?: string;
  textStyle?: TextStyle;
  imageUrl?: string;
  ratingLimits: {
    isRatable: boolean;
    lowerLimit: number;
    upperLimit: number;
    optionRatings?: number[];
  }
}

interface FormData {
  title: string;
  description?: string;
  fields: FormField[];
}

interface OptionsInputProps {
  field: FormField;
  index: number;
  updateField: (index: number, updates: Partial<FormField>) => void;
}

const OptionsInput: React.FC<OptionsInputProps> = ({ field, index, updateField }) => {
  const initializeRatings = (options: string[]): number[] => {
    return Array(options.length).fill(0);
  };

  const addOption = (): void => {
    const currentOptions = field?.options || [];
    const currentRatingLimits = field?.ratingLimits || { isRatable: true };
    const currentRatings = currentRatingLimits?.optionRatings || [];

    const newOptions = [...currentOptions, ''];
    const newRatings = [...currentRatings, 0];

    updateField(index, {
      options: newOptions,
      ratingLimits: {
        ...currentRatingLimits,
        isRatable: true,
        lowerLimit: 0,
        upperLimit: 0,
        optionRatings: newRatings
      }
    });
  };

  const removeOption = (optionIndex: number): void => {
    const currentOptions = field?.options || [];
    const currentRatingLimits = field?.ratingLimits || { isRatable: true };
    const currentRatings = currentRatingLimits?.optionRatings || [];

    const newOptions = currentOptions.filter((_, i) => i !== optionIndex);
    const newRatings = currentRatings.filter((_, i) => i !== optionIndex);

    updateField(index, {
      options: newOptions,
      ratingLimits: {
        ...currentRatingLimits,
        isRatable: true,
        lowerLimit: 0,
        upperLimit: 0,
        optionRatings: newRatings
      }
    });
  };

  const updateOption = (optionIndex: number, value: string): void => {
    const currentOptions = field?.options || [];
    const newOptions = [...currentOptions];
    newOptions[optionIndex] = value;
    updateField(index, { options: newOptions });
  };

  const updateRating = (optionIndex: number, value: number): void => {
    const currentRatingLimits = field?.ratingLimits || { isRatable: true };
    const currentOptions = field?.options || [];
    let newRatings = [...(currentRatingLimits?.optionRatings || initializeRatings(currentOptions))];
    newRatings[optionIndex] = value;

    updateField(index, {
      ratingLimits: {
        ...currentRatingLimits,
        isRatable: true,
        lowerLimit: 0,
        upperLimit: 0,
        optionRatings: newRatings
      }
    });
  };

  useEffect(() => {
    if (field?.ratingLimits?.isRatable && field?.options &&
      (!field.ratingLimits?.optionRatings ||
        field.ratingLimits.optionRatings.length !== field.options.length)) {
      updateField(index, {
        ratingLimits: {
          ...field.ratingLimits,
          isRatable: true,
          lowerLimit: 0,
          upperLimit: 0,
          optionRatings: initializeRatings(field.options)
        }
      });
    }
  }, []);

  const options = field?.options || [];
  const ratingLimits = field?.ratingLimits || { isRatable: true };
  const optionRatings = ratingLimits?.optionRatings || [];

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Options</label>

      <div className="space-y-2">
        {options.map((option: string, optionIndex: number) => (
          <div key={optionIndex} className="flex items-center space-x-2">
            <input
              type="text"
              value={option}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateOption(optionIndex, e.target.value)}
              className="flex-1 p-2 border rounded"
              placeholder={`Option ${optionIndex + 1}`}
            />
            <input
              type="number"
              value={optionRatings[optionIndex] || 0}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateRating(optionIndex, parseInt(e.target.value))}
              className="w-20 p-2 border rounded"
            />
            <button
              onClick={() => removeOption(optionIndex)}
              className="text-red-500 hover:text-red-700"
              type="button"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={addOption}
        className="flex items-center space-x-1 text-blue-500 hover:text-blue-700"
        type="button"
      >
        <PlusCircle className="w-5 h-5" />
        <span>Add Option</span>
      </button>

      {options.length > 0 && (
        <div className="mt-2 space-y-1">
          <div className="text-sm font-medium text-gray-700">Preview:</div>
          <div className="space-y-1">
            {field?.type === "radio" &&
              options.map((option: string, i: number) => (
                <label key={i} className="flex items-center space-x-2">
                  <input type="radio" name={`preview-${field?.id}`} className="text-blue-500" />
                  <span className="text-sm">
                    {option}
                  </span>
                </label>
              ))}
            {field?.type === "select" && (
              <select className="w-full p-2 border rounded">
                {options.map((option: string, i: number) => (
                  <option key={i} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const renderTextWithNewlines = (text: string) => {
  return text.split("\n").map((line, index) => (
    <React.Fragment key={index}>
      {line}
      <br />
    </React.Fragment>
  ));
};

const InfoFieldInput = memo(({ field, index, updateField }: { field: FormField; index: number; updateField: (index: number, updates: Partial<FormField>) => void }) => {
  const textStyles: TextStyle[] = ["normal", "bold", "italic"];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <textarea placeholder="Information content" className="w-full p-2 border rounded col-span-2" value={field.content || ""} onChange={(e) => updateField(index, { content: e.target.value })} rows={3} />
        <select className="p-2 border rounded" value={field.textStyle || "normal"} onChange={(e) => updateField(index, { textStyle: e.target.value as TextStyle })}>
          {textStyles.map((style) => (
            <option key={style} value={style}>
              {style.charAt(0).toUpperCase() + style.slice(1)}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-2">
        <div className="text-sm font-medium text-gray-700">Preview:</div>
        <div className={`p-4 bg-gray-50 rounded ${field.textStyle === "bold" ? "font-bold" : field.textStyle === "italic" ? "italic" : ""}`}>
          <span>{renderTextWithNewlines(field.content ?? "Preview of your informational text will appear here")}</span>
        </div>
      </div>
    </div>
  );
});

const ImageField = ({ field, index, updateField }: { field: FormField; index: number; updateField: (index: number, updates: Partial<FormField>) => void }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const authenticatedFetch = useAuthenticatedFetch();

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setUploadError("Please upload an image file");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await authenticatedFetch("/api/image/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      updateField(index, { imageUrl: data.imageUrl });
    } catch (error) {
      setUploadError("Failed to upload image");
    }
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setUploadError("");

      if (!e.dataTransfer) return;
      const file = e.dataTransfer.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        setUploadError("Please upload an image file");
        return;
      }

      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await authenticatedFetch("/api/image/upload", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) throw new Error("Upload failed");

        const data = await response.json();
        updateField(index, { imageUrl: data.imageUrl });
      } catch (error) {
        setUploadError("Failed to upload image");
      }
    },
    [index, updateField]
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError("");
    const file = e.target.files?.[0];

    if (!file) return;

    await handleFileUpload(file);
  };

  const dragDropProps = {
    onDragEnter: handleDrag,
    onDragLeave: handleDrag,
    onDragOver: handleDrag,
    onDrop: handleDrop,
  };

  return (
    <div className="space-y-3">
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*" className="hidden" />
      <div
        onClick={handleClick}
        {...dragDropProps}
        className={`
                    border-2 border-dashed rounded-lg p-8
                    flex flex-col items-center justify-center space-y-2
                    cursor-pointer transition-colors
                    ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"}
                    ${field.imageUrl ? "bg-gray-50" : "bg-white"}
                `}
      >
        {field.imageUrl ? (
          <div className="space-y-4 w-full" {...dragDropProps}>
            <img src={field.imageUrl} alt="Uploaded preview" className="max-w-full max-h-64 mx-auto object-contain" {...dragDropProps} />
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                updateField(index, { imageUrl: undefined });
              }}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove Image
            </button>
          </div>
        ) : (
          <>
            <ImageIcon size={48} className="text-gray-400" />
            <p className="text-sm text-gray-500" {...dragDropProps}>
              Drag and drop an image here, or click to select
            </p>
          </>
        )}
      </div>
      {uploadError && <p className="text-red-500 text-sm">{uploadError}</p>}
    </div>
  );
};

const Field = memo(({ field, index, updateField, removeField, fieldTypes }: { field: FormField; index: number; updateField: (index: number, updates: Partial<FormField>) => void; removeField: (index: number) => void; fieldTypes: FieldType[] }) => {
  const [gradingRange, setGradingRange] = useState<number[]>([0, 10]);
  const [isGradable, setIsGradable] = useState<boolean>(true);

  const handleGradingSliderChange = (event: Event, newValue: number | number[]) => {
    const limits = (newValue as number[]).sort();
    setGradingRange(limits);
    field.ratingLimits.lowerLimit = limits[0];
    field.ratingLimits.upperLimit = limits[1];
    updateField(index, {
      ratingLimits: {
        isRatable: isGradable,
        lowerLimit: gradingRange[0],
        upperLimit: gradingRange[1] - 1
      }
    });
  };

  const handleGradingCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsGradable(event.target.checked);
    field.ratingLimits.isRatable = event.target.checked;
    updateField(index, {
      ratingLimits: {
        isRatable: event.target.checked,
        lowerLimit: gradingRange[0],
        upperLimit: gradingRange[1]
      }
    });
  };

  return (
    <div className="p-4 border rounded space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Field {index + 1}</h3>
        <button type="button" onClick={() => removeField(index)} className="text-red-500 hover:text-red-700">
          <Trash2 size={20} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <input type="text" placeholder="Field Label" required className="p-2 border rounded" value={field.label} onChange={(e) => updateField(index, { label: e.target.value })} />
        <select
          className="p-2 border rounded"
          value={field.type}
          onChange={(e) =>
            updateField(index, {
              type: e.target.value as FieldType,
              required: e.target.value === "info" ? false : field.required,
            })
          }
        >
          {fieldTypes.map((type) => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {field.type !== "info" && field.type !== "image-info" && (
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => updateField(index, { required: e.target.checked })}
            />
            <span>Required</span>
          </label>

          {!["select", "radio"].includes(field.type) && (
            <>
              <input
                type="text"
                placeholder="Placeholder (optional)"
                className="flex-1 p-2 border rounded"
                value={field.placeholder || ""}
                onChange={(e) => updateField(index, { placeholder: e.target.value })}
              />
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={field.ratingLimits.isRatable}
                  onChange={(event) => { handleGradingCheckboxChange(event) }}
                />
                <span>Is Gradable?</span>
              </label>
              <label>
                <Slider
                  value={[field.ratingLimits.lowerLimit, field.ratingLimits.upperLimit]}
                  disabled={!field.ratingLimits.isRatable}
                  onChange={handleGradingSliderChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={10}
                />
                <span>Grade Limits</span>
              </label>
            </>
          )}
        </div>
      )}


      {(field.type === "select" || field.type === "radio") && <OptionsInput field={field} index={index} updateField={updateField} />}

      {field.type === "info" && <InfoFieldInput field={field} index={index} updateField={updateField} />}

      {field.type === "image-info" && <ImageField field={field} index={index} updateField={updateField} />}
    </div>
  );
});

function FormBuilder() {
  const [formData, setFormData] = useState<FormData>({
    title: "",
    description: "",
    fields: [],
  });
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showSnackbar } = useSnackbar();
  const authenticatedFetch = useAuthenticatedFetch();

  const [formId, setFormId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (state) {
      setFormData(state);
      setFormId((state as FormModel)._id);
    }
  }, []);

  const fieldTypes: FieldType[] = ["text", "number", "email", "checkbox", "select", "textarea", "radio", "info", "image-info"];

  const addField = () => {
    const newField: FormField = {
      id: `field-${formData.fields.length + 1}`,
      type: "text",
      label: "",
      required: false,
      textStyle: "normal",
      ratingLimits: {
        isRatable: true,
        lowerLimit: 0,
        upperLimit: 10
      }
    };
    setFormData((prev) => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    setFormData((prev) => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], ...updates };

      return { ...prev, fields: newFields };
    });
  };

  const removeField = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const saveForm = async () => {
    try {
      const response = await authenticatedFetch("/api/forms", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showSnackbar("Form saved successfully!", "success");
        const returnedForm: any = response.json();
        setFormId(returnedForm._id);
      } else {
        throw new Error("Failed to save form");
      }
    } catch (error) {
      showSnackbar("Error saving form", "error");
    }
  };

  const updateForm = async (formId: string) => {
    try {
      const response = await authenticatedFetch(`/api/forms/${formId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showSnackbar("Form updated successfully!", "success");
      } else {
        throw new Error("Failed to save form");
      }
    } catch (error) {
      showSnackbar("Error updating form", "error");
    }
  };

  const handleSubmit = async (event: any, formId: string | undefined = undefined) => {
    event.preventDefault();
    formId ? await updateForm(formId) : await saveForm();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={event => handleSubmit(event, formId)}>
        <div className="space-y-6">
          <div className="flex flex-row items-center">
            <Tooltip title="Back">
              <IconButton onClick={() => navigate("/dashboard")} aria-label="Back">
                <ArrowLeftIcon />
              </IconButton>
            </Tooltip>
            <h1 className="text-2xl font-bold">Form Builder</h1>
          </div>

          <div className="space-y-4">
            <input type="text" placeholder="Form Title" className="w-full p-2 border rounded" value={formData.title} required onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))} />
            <textarea placeholder="Form Description (optional)" className="w-full p-2 border rounded" value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} />
          </div>

          <div className="space-y-4">
            {formData.fields.map((field, index) => (
              <Field key={field.id} field={field} index={index} updateField={updateField} removeField={removeField} fieldTypes={fieldTypes} />
            ))}

            <button onClick={addField} type="button" className="flex items-center space-x-2 text-orange-500 hover:text-blue-700">
              <PlusCircle size={20} />
              <span>Add Field</span>
            </button>
          </div>

          <button type="submit" disabled={!formData.title || formData.fields.length === 0} className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-400">
            <Save size={20} />
            <span>Save Form</span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default FormBuilder;
